import React from "react";
import { Helmet } from "react-helmet-async";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import PlanPicker from "components/pricing/TwoPlansWithDurationSwitcher.js";
import FAQ from "components/faqs/SingleCol.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";

/*
 * Pricing page. Previously a template hero (stock illustration and an email
 * field) above the same picker the homepage already showed. Now it carries
 * what only a pricing page should: the picker, the full feature list and the
 * billing questions buyers ask.
 *
 * Every statement here is verified against the app and the live Stripe
 * configuration (1 Oct 2026). VAT and cancellation terms are deliberately
 * absent until they are confirmed.
 */
const Intro = tw.div`max-w-3xl mx-auto text-center pt-12`;
const H1 = tw.h1`text-4xl sm:text-5xl font-black text-primary-900 leading-tight`;
const Lead = tw.p`mt-6 text-lg text-gray-800`;

const Section = tw.div`bg-gray-100 -mx-8 px-8`;
const H2 = tw.h2`text-center text-3xl sm:text-4xl font-black text-secondary-500`;
const SubLead = tw.p`mt-4 text-center text-lg text-gray-800 max-w-2xl mx-auto`;
const Groups = tw.div`mt-12 grid gap-6 md:grid-cols-2 max-w-screen-lg mx-auto`;
const Group = tw.div`rounded-xl bg-white border border-gray-200 p-8`;
const GroupTitle = tw.h3`text-xl font-bold text-gray-900`;
const List = tw.ul`mt-4 space-y-3`;
const Item = tw.li`flex items-start gap-3 text-gray-800`;
const Tick = tw(CheckboxIcon)`w-5 h-5 mt-0.5 text-primary-500 flex-shrink-0`;

const INCLUDED = [
  { title: "Reporting", items: [
    "Anonymous or named reporting",
    "One-time access key for anonymous follow-up",
    "Evidence uploads with each report",
    "Disguise Mode to hide the page instantly",
  ]},
  { title: "Case management", items: [
    "Assign and reassign cases between caseworkers",
    "Open, in progress, pending information, resolved and closed",
    "Internal notes the reporter never sees",
    "Two-way messaging, including with anonymous reporters",
  ]},
  { title: "Privacy and security", items: [
    "Role-based access for admins, caseworkers, leadership and employees",
    "Append-only audit trail of every case view and change",
    "Automatic privacy blur on unattended screens",
    "Data hosted in the UK (London)",
  ]},
  { title: "Insight and administration", items: [
    "Leadership analytics that never reveal who reported",
    "Company policy and handbook library",
    "Your logo, colour and welcome message",
    "Sign in with Microsoft, or by email",
  ]},
];

const PRICING_FAQS = [
  { question: "How do I choose the right plan?",
    answer: "Pick the band that matches the number of employees in your organisation. Every plan includes every feature — the only difference between them is organisation size." },
  { question: "Do you charge per user?",
    answer: "No. Pricing is per organisation. There's no extra charge for adding caseworkers, administrators or leadership users, and every employee can report." },
  { question: "Is paying annually cheaper?",
    answer: "No — the price is the same either way. Annual billing is one payment a year; monthly billing spreads the same amount across twelve payments." },
  { question: "Is there a free trial?",
    answer: "We don't offer a self-serve trial. Book a demo instead and we'll walk you through the whole platform using a sample organisation." },
  { question: "How do I pay?",
    answer: "Payment is taken securely by Stripe when you sign up. If you've been given a promotion code, you can enter it at checkout." },
  { question: "We have more than 20,000 employees. Can we use EthosHub?",
    answer: "Yes. Get in touch and we'll put together a plan for an organisation of your size." },
];

export default function PricingPage() {
  return (
    <AnimationRevealPage>
      <Helmet>
        <title>Pricing | EthosHub</title>
        <meta name="description" content="EthosHub pricing: one plan with every feature, priced by organisation size and never per user. From £10,000 a year, billed monthly or annually." />
        <link rel="canonical" href="https://www.ethoshub.io/PricingPage" />
        <meta property="og:title" content="Pricing | EthosHub" />
        <meta property="og:description" content="One plan, every feature, priced by organisation size — never per user." />
        <meta property="og:url" content="https://www.ethoshub.io/PricingPage" />
      </Helmet>
      <Header />

      <Intro>
        <H1>Pricing</H1>
        <Lead>
          One plan with every feature included. You choose your organisation's size and
          how you'd like to pay — that's the only decision to make.
        </Lead>
      </Intro>

      <PlanPicker showHeading={false} />

      <Section>
        <Container>
          <ContentWithPaddingXl>
            <H2>Everything included, on every plan</H2>
            <SubLead>No feature is held back for a bigger organisation or a higher price.</SubLead>
            <Groups>
              {INCLUDED.map(g => (
                <Group key={g.title}>
                  <GroupTitle>{g.title}</GroupTitle>
                  <List>
                    {g.items.map(i => <Item key={i}><Tick aria-hidden="true" />{i}</Item>)}
                  </List>
                </Group>
              ))}
            </Groups>
          </ContentWithPaddingXl>
        </Container>
      </Section>

      <FAQ heading="Pricing questions" description="Straight answers about plans and billing." faqs={PRICING_FAQS} />
      <Footer />
    </AnimationRevealPage>
  );
}
